import { useEffect, useMemo, useRef, useState } from 'react'
import { useDialogFocus } from '@/hooks/useDialogFocus'
import { X, Check, NotebookPen, HelpCircle, Link2, ChevronLeft, ChevronRight, CalendarCheck2, AlertCircle } from 'lucide-react'
import { parseLocalYmd } from '@/lib/cadence/config'
import { pageMonthStart, pageSeasonStart, planWindowDates, rewindowPlanItems, type PlanItem, type PlanPlacement, type PageAltitude, type PageReviewPayload } from '@/lib/planParse'
import { normalizeSeasons, readSeasons, seasonLabel, nextSeasonStart, seasonStartFor, type Seasons } from '@/lib/cadence/seasons'
import { findLikelyDuplicate, isSameLine, type ExistingTask } from '@/lib/planDuplicates'
import { DOMAINS, type DomainId } from '@/lib/domains'
import type { TitlePeriod } from '@/lib/planTitle'
import type { PageNote } from '@/lib/pageParse'
import type { FamilyMember } from '@/types/family'
import { hasItemType, inferredCategory, isGoalRow, itemTypeOf, itemTypeProblem, normalizeForSave, withItemType, PAPER_ITEM_TYPES, type PaperItemType, type TypeDrafts } from '@/lib/paperItemType'
import { goalJoinsDraft } from '@/lib/planning/paperIntoDraft'
import { ItemTypeSelect, RoutineDaysPicker } from './ItemTypeControls'
import { assigneeOptions, initialAssignee, UNASSIGNED } from '@/lib/paperAssignee'

// The payload's shape lives with the rest of the page-from-paper model.
export type { PageReviewPayload } from '@/lib/planParse'

export interface PageReviewSheetProps {
  /** Parsed actions, in page order. */
  items: PlanItem[]
  /** Parsed prose, in page order. */
  notes: PageNote[]
  /** Lines the model could not read. Read-only until promoted. */
  unclear: string[]
  /** The SAME dates the parser was allowed to place on (local YYYY-MM-DD). */
  windowDates: string[]
  /** Which page this was read as. A year page's lines may be goals. */
  altitude?: PageAltitude
  /** The household's season boundaries (for the season chip). Default: the cached ones. */
  seasons?: Seasons
  /** When the page was snapped — decides which month/season it is for. Default: now. */
  today?: Date
  /** The period the page's own HEADING names — it beats the calendar guess. */
  titlePeriod?: TitlePeriod
  /** The heading as written, so the sheet can say why it opened where it did. */
  pageTitle?: string | null
  /** Open tasks a line might already be — the Use existing item / Add as new offer. */
  existingTasks?: ExistingTask[]
  /** 'YYYY-MM-DD' → the day's event titles, for day-facts already on the calendar. */
  calendarTitlesByDay?: Map<string, string[]>
  /** The domain to open on. Default: the one remembered for this altitude, else Family. */
  initialDomain?: DomainId
  members: FamilyMember[]
  /** The signed-in user's member id, found by auth identity (signedInMember).
   *  Unnamed lines start on it; null = not known, and they start Unassigned. */
  currentMemberId?: string | null
  committing: boolean
  /** Called with only the checked rows, as edited. */
  onCommit: (payload: PageReviewPayload) => void
  /** What to call the plan being written that this page would join, for the
   *  period the chip is on ("this week", "October"). Undefined = no session
   *  in progress for that period; the offer is not made. Asked per chip, so
   *  moving the chip to an unplanned month withdraws the offer. */
  draftLabelFor?: (chosenStart: Date | null) => string | undefined
  /** Same payload as onCommit, taken into that draft instead of committed. */
  onAddToDraft?: (payload: PageReviewPayload) => void
  onClose: () => void
}

interface ItemRow extends PlanItem {
  included: boolean
  /** The open task this line probably repeats, if any. */
  dup?: ExistingTask | null
  /** "Add as new" was pressed — the offer is done with. */
  dupDismissed?: boolean
  /** Where-and-when kept for each type this row has been, so switching back
   *  restores it. Never saved. */
  typeDrafts?: TypeDrafts
  /** The page named no one and nobody has picked yet — the assignee is the
   *  signed-in default, filled in if that identity arrives after opening. */
  assigneeDefaulted?: boolean
}
interface NoteRow extends PageNote { included: boolean }


function placementValue(p: PlanPlacement): string {
  return p.kind === 'date' ? p.date : p.kind
}

function placementFromValue(v: string): PlanPlacement {
  switch (v) {
    case 'week': case 'month': case 'season': case 'someday': case 'inbox': case 'goal':
      return { kind: v }
    default:
      return { kind: 'date', date: v }
  }
}

/** The horizon placements every page may use, in ladder order. */
const HORIZON_OPTIONS: { value: PlanPlacement['kind']; label: string }[] = [
  { value: 'inbox', label: 'Inbox' },
  { value: 'week', label: 'This week' },
  { value: 'month', label: 'This month' },
  { value: 'season', label: 'This season' },
  { value: 'someday', label: 'Someday' },
]

const ALTITUDE_BLURB: Record<PageAltitude, string> = {
  week: 'Check what Symphony read before it changes the week.',
  month: "Read as a month page — undated lines go on the month's list.",
  season: "Read as a season page — undated lines go on the season's list.",
  year: 'Read as a year page — outcomes and projects are goals for the year; set anything else to what it is.',
}

/** Where a row goes when its day is cleared: the page's own list (a year
 *  page has none, so Someday). */
function undatedPlacement(altitude: PageAltitude): PlanPlacement {
  return altitude === 'year' ? { kind: 'someday' } : { kind: altitude }
}

function dateLabel(ymd: string): string {
  return parseLocalYmd(ymd).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })
}


/** A day-fact and a calendar entry rarely word it the same: "No school —
 *  Labor Day" against "Labor Day". Strip the framing and compare what's left. */
function factKey(s: string): string {
  return s.toLowerCase()
    .replace(/no school/g, ' ')
    .replace(/holiday/g, ' ')
    .replace(/[^a-z0-9\s]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
}

/** Is this day-fact already on the day's calendar? Then it is not news. */
function onCalendar(item: PlanItem, byDay?: Map<string, string[]>): boolean {
  if (!byDay || item.kind !== 'dayfact' || item.placement.kind !== 'date') return false
  const titles = byDay.get(item.placement.date)
  if (!titles?.length) return false
  const a = factKey(item.title)
  if (!a) return false
  return titles.some((t) => {
    const b = factKey(t)
    return !!b && (a.includes(b) || b.includes(a))
  })
}

const DOMAIN_KEY = (altitude: PageAltitude) => `symphony.paper.domain.${altitude}`

function rememberedDomain(altitude: PageAltitude): DomainId | null {
  try {
    const raw = localStorage.getItem(DOMAIN_KEY(altitude))
    return DOMAINS.some((d) => d.id === raw) ? (raw as DomainId) : null
  } catch {
    return null
  }
}

/**
 * The review step of page-from-paper: everything the parser read, editable,
 * nothing written until "Add". Handwriting parsing will misread sometimes —
 * this sheet is where trust in the pipeline lives. Unclear lines sit apart
 * and inert precisely because a wrong task costs more than an unread line.
 */
export function PageReviewSheet({
  items, notes, unclear, windowDates, altitude = 'week', seasons = readSeasons(), today = new Date(),
  titlePeriod = null, pageTitle = null, existingTasks = [], calendarTitlesByDay,
  initialDomain, members, currentMemberId = null, committing, onCommit, onClose, draftLabelFor, onAddToDraft,
}: PageReviewSheetProps) {
  // The picker: you once (by id), everyone else, and a real Unassigned. Every
  // row's assignee is decided here, so the save writes exactly what is shown.
  const memberIds = useMemo(() => new Set(members.map((m) => m.id)), [members])
  const meId = currentMemberId && memberIds.has(currentMemberId) ? currentMemberId : null
  const peopleOptions = useMemo(() => assigneeOptions(members, meId), [members, meId])
  // A caller's boundaries may be hand-written and out of calendar order; the
  // season maths below assume ordered ones.
  const seasonsOrdered = useMemo(() => normalizeSeasons(seasons), [seasons])
  // Focus in, Escape closes (the first Escape just leaves a field being edited).
  const dialogRef = useRef<HTMLDivElement>(null)
  useDialogFocus(true, dialogRef, onClose)
  // Day-facts the calendar already knows are set aside before anything else:
  // "No school Monday" written on a page whose event is already on the day is
  // a confirmation, not a task.
  const [alreadyOnCalendar] = useState<PlanItem[]>(() => items.filter((i) => onCalendar(i, calendarTitlesByDay)))
  const [itemRows, setItemRows] = useState<ItemRow[]>(() =>
    items
      .filter((i) => !onCalendar(i, calendarTitlesByDay))
      // The type the badge used to only show is stamped on the row, so the
      // one on screen is the one saved.
      .map((i) => {
        const dup = findLikelyDuplicate(i.title, existingTasks)
        return {
          ...i,
          ...(i.kind === 'task' && !isGoalRow(i) && !i.category ? { category: inferredCategory(i) } : {}),
          assigneeId: initialAssignee(i, memberIds, meId),
          assigneeDefaulted: !(i.assigneeId && memberIds.has(i.assigneeId)),
          included: true,
          dup,
          // A word-for-word repeat of something already on the plan starts as
          // that item (nothing new saved); "Don't use it" makes it new again.
          ...(dup && !i.sourceId && i.kind !== 'dayfact' && isSameLine(i.title, dup.title) ? { sourceId: dup.id } : {}),
        }
      }),
  )
  // The household can finish loading after the sheet opens: rows still on the
  // default then take the signed-in member. A row someone picked is left alone.
  useEffect(() => {
    if (!meId) return
    setItemRows((rows) => (rows.some((r) => r.assigneeDefaulted && r.assigneeId === null)
      ? rows.map((r) => (r.assigneeDefaulted && r.assigneeId === null ? { ...r, assigneeId: meId } : r))
      : rows))
  }, [meId])
  const [noteRows, setNoteRows] = useState<NoteRow[]>(() => notes.map((n) => ({ ...n, included: true })))
  const [unread, setUnread] = useState<string[]>(() => unclear)
  const [domain, setDomain] = useState<DomainId>(() => initialDomain ?? rememberedDomain(altitude) ?? 'family')
  // Which month / season the page is FOR. The page's own heading wins; failing
  // that it is guessed from the date (a page snapped in a month's last week is
  // for the coming month). One tap either way to fix.
  const [monthStart, setMonthStart] = useState<Date>(() =>
    titlePeriod?.kind === 'month' && altitude === 'month' ? titlePeriod.start : pageMonthStart(today))
  const [seasonStart, setSeasonStart] = useState<Date>(() =>
    titlePeriod?.kind === 'season' && altitude === 'season' ? titlePeriod.start : pageSeasonStart(today, seasonsOrdered))

  // The dates this page may place on: a month/season page's window follows the
  // chip, so flipping it opens dates the parser never saw.
  const windowNow = useMemo(() => (
    altitude === 'month' ? planWindowDates(today, 'month', seasonsOrdered, monthStart)
      : altitude === 'season' ? planWindowDates(today, 'season', seasonsOrdered, seasonStart)
        : windowDates
  ), [altitude, today, seasonsOrdered, monthStart, seasonStart, windowDates])

  // Flipping the chip re-places every row against the new window without a
  // second model call: a Dec 12 line degraded off the Fall list becomes a
  // dated row the moment Winter is chosen (and back again).
  const rewindowTo = (periodStart: Date) => {
    const win = planWindowDates(today, altitude, seasonsOrdered, periodStart)
    // rewindowPlanItems returns each row spread, so `included` / `dup` ride
    // along. A goal is never scheduled, so it keeps its list whatever the chip.
    setItemRows((rows) => rows.map((r) => (isGoalRow(r) ? r : rewindowPlanItems([r], win, altitude)[0] as ItemRow)))
  }
  const shiftMonth = (by: number) => {
    const next = new Date(monthStart.getFullYear(), monthStart.getMonth() + by, 1)
    setMonthStart(next)
    rewindowTo(next)
  }
  const shiftSeason = (by: number) => {
    let next: Date
    if (by > 0) next = nextSeasonStart(seasonStart, seasonsOrdered)
    else {
      const dayBefore = new Date(seasonStart)
      dayBefore.setDate(dayBefore.getDate() - 1)
      next = seasonStartFor(dayBefore, seasonsOrdered)
    }
    setSeasonStart(next)
    rewindowTo(next)
  }

  const includedCount = useMemo(
    () => itemRows.filter((r) => r.included).length + noteRows.filter((r) => r.included).length,
    [itemRows, noteRows],
  )
  // What each checked row will be saved as — its selected type, exactly. A
  // linked row saves nothing of its own and is counted apart.
  const typeCounts = useMemo(() => {
    const counts: Record<PaperItemType, number> = { goal: 0, task: 0, appointment: 0, activity: 0, routine: 0 }
    for (const r of itemRows) {
      if (r.included && hasItemType(r) && !r.sourceId) counts[itemTypeOf(r)] += 1
    }
    return counts
  }, [itemRows])
  const linkedCount = useMemo(() => itemRows.filter((r) => r.included && r.sourceId).length, [itemRows])
  const summary = useMemo(() => {
    const includedDays = itemRows.filter((r) => r.included && r.kind === 'dayfact').length
    const includedNotes = noteRows.filter((r) => r.included).length
    const n = (count: number, one: string, many = `${one}s`) => (count > 0 ? `${count} ${count === 1 ? one : many}` : null)
    const typed = [
      n(typeCounts.goal, 'goal or project', 'goals or projects'),
      n(typeCounts.task, 'action'),
      n(typeCounts.appointment, 'appointment'),
      n(typeCounts.activity, 'activity', 'activities'),
      n(typeCounts.routine, 'routine'),
    ]
    return [
      ...(typed.some(Boolean) ? typed : ['nothing new']),
      linkedCount > 0 ? `${linkedCount} already on your plan` : null,
      n(includedDays, 'day note'),
      n(includedNotes, 'note'),
      unread.length > 0 ? `${unread.length} unclear` : null,
    ].filter(Boolean).join(' / ')
  }, [itemRows, noteRows, unread.length, typeCounts, linkedCount])
  // Rows that cannot save as the type chosen: a routine with no days, an
  // appointment with no day. A linked row saves nothing, so it has none.
  const problemRows = useMemo(
    () => itemRows.filter((r) => r.included && !r.sourceId && itemTypeProblem(r)),
    [itemRows],
  )
  const problems = problemRows.length
  const problemText = useMemo(() => {
    const routines = problemRows.filter((r) => itemTypeOf(r) === 'routine').length
    const appts = problemRows.length - routines
    return [
      routines ? (routines === 1 ? '1 routine needs its days' : `${routines} routines need their days`) : null,
      appts ? (appts === 1 ? '1 appointment needs its day' : `${appts} appointments need their days`) : null,
    ].filter(Boolean).join(' and ')
  }, [problemRows])
  const isEmpty = itemRows.length === 0 && noteRows.length === 0 && unread.length === 0 && alreadyOnCalendar.length === 0

  const updateItem = (index: number, patch: Partial<ItemRow>) =>
    setItemRows((prev) => prev.map((r, i) => (i === index ? { ...r, ...patch } : r)))
  // A linked row IS the existing item: it has no type of its own to change.
  const changeType = (index: number, type: PaperItemType) =>
    setItemRows((prev) => prev.map((r, i) => (i !== index || r.sourceId ? r : withItemType(r, type, altitude))))

  // Several lines at once (Scott, 2026-09-28: "select, and reclassify in bulk
  // — all selected items → goal or project"). Selecting is its own mode, so
  // the row's checkbox keeps meaning one thing: add this line. A day note or a
  // line matched to an existing item has no type to change and is skipped.
  const [selecting, setSelecting] = useState(false)
  const [selected, setSelected] = useState<Set<number>>(() => new Set())
  const retypable = (r: ItemRow) => !r.sourceId && r.kind !== 'dayfact'
  const toggleSelected = (i: number) => setSelected((prev) => { const next = new Set(prev); if (next.has(i)) next.delete(i); else next.add(i); return next })
  const bulkType = (type: PaperItemType) =>
    setItemRows((prev) => prev.map((r, i) => (selected.has(i) && retypable(r) ? withItemType(r, type, altitude) : r)))
  const bulkInclude = (included: boolean) =>
    setItemRows((prev) => prev.map((r, i) => (selected.has(i) ? { ...r, included } : r)))
  const endSelecting = () => { setSelecting(false); setSelected(new Set()) }
  const updateNote = (index: number, patch: Partial<NoteRow>) =>
    setNoteRows((prev) => prev.map((r, i) => (i === index ? { ...r, ...patch } : r)))

  const promoteToTask = (line: string) => {
    setItemRows((prev) => [...prev, { title: line, placement: { kind: 'inbox' }, time: null, assigneeId: meId, assigneeDefaulted: true, note: null, dateHint: null, kind: 'task', category: 'task', recurring: null, phone: null, contactMemberId: null, included: true, dup: findLikelyDuplicate(line, existingTasks) }])
    setUnread((prev) => prev.filter((l) => l !== line))
  }
  const promoteToNote = (line: string) => {
    setNoteRows((prev) => [...prev, { title: line, content: line, included: true }])
    setUnread((prev) => prev.filter((l) => l !== line))
  }

  const buildPayload = (): PageReviewPayload => {
    // The house remembers what kind of page this altitude usually is.
    try { localStorage.setItem(DOMAIN_KEY(altitude), domain) } catch { /* private mode */ }
    return {
      domain,
      items: itemRows
        .filter((r) => r.included && r.title.trim())
        .map(({ included: _included, dup: _dup, dupDismissed: _dupDismissed, assigneeDefaulted: _assigneeDefaulted, ...item }) => normalizeForSave({ ...item, title: item.title.trim() })),
      notes: noteRows
        .filter((r) => r.included && r.content.trim())
        .map(({ included: _included, ...note }) => ({ title: note.title.trim(), content: note.content.trim() })),
      ...(altitude === 'month' ? { monthStart } : {}),
      ...(altitude === 'season' ? { seasonStart } : {}),
    }
  }

  const blocked = committing || includedCount === 0 || problems > 0
  const commit = () => { if (!blocked) onCommit(buildPayload()) }
  const chosenStart = altitude === 'month' ? monthStart : altitude === 'season' ? seasonStart : null
  const draftLabel = draftLabelFor?.(chosenStart)
  // The page joins the plan being written instead of landing on the list
  // directly — the same rows, one destination up the flow.
  const addToDraft = () => { if (!blocked) onAddToDraft?.(buildPayload()) }
  // What the draft cannot hold keeps its type by being saved directly: every
  // appointment, activity and routine, and a goal for another period's list.
  const goalsElsewhere = itemRows.filter((r) => r.included && !r.sourceId && isGoalRow(r) && !goalJoinsDraft(r, altitude)).length
  const savedDirectly = typeCounts.appointment + typeCounts.activity + typeCounts.routine + goalsElsewhere
  const monthName = monthStart.toLocaleDateString('en-US', { month: 'long', ...(monthStart.getFullYear() !== today.getFullYear() ? { year: 'numeric' } : {}) })
  const seasonName = seasonLabel(seasonStart, seasonsOrdered)

  // The period chip: ‹ September › / ‹ Fall 2026 › — which list this page fills.
  const periodChip = (altitude === 'month' || altitude === 'season') && (
    <span className="inline-flex items-center gap-0.5 rounded-lg bg-neutral-100 px-1 py-0.5 text-[13px] font-medium text-neutral-700">
      <button type="button" aria-label={altitude === 'month' ? 'Previous month' : 'Previous season'}
        onClick={() => (altitude === 'month' ? shiftMonth(-1) : shiftSeason(-1))}
        className="p-0.5 rounded text-neutral-400 hover:text-neutral-700 hover:bg-neutral-200/60">
        <ChevronLeft className="w-3.5 h-3.5" />
      </button>
      <span className="px-1">
        {altitude === 'month'
          ? monthStart.toLocaleDateString('en-US', { month: 'long', ...(monthStart.getFullYear() !== today.getFullYear() ? { year: 'numeric' } : {}) })
          : seasonLabel(seasonStart, seasonsOrdered)}
      </span>
      <button type="button" aria-label={altitude === 'month' ? 'Next month' : 'Next season'}
        onClick={() => (altitude === 'month' ? shiftMonth(1) : shiftSeason(1))}
        className="p-0.5 rounded text-neutral-400 hover:text-neutral-700 hover:bg-neutral-200/60">
        <ChevronRight className="w-3.5 h-3.5" />
      </button>
    </span>
  )

  return (
    <div className="fixed inset-0 z-[60] bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="bg-bg-elevated rounded-2xl shadow-2xl w-full max-w-2xl max-h-[85vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-label="Review page items"
      >
        <div className="flex items-center justify-between gap-4 px-5 py-4 border-b border-neutral-200/60">
          <div className="flex items-center gap-2">
            <NotebookPen className="w-5 h-5 text-primary-600" />
            <div>
              <h3 className="font-display text-xl text-neutral-900">From your page</h3>
              {!isEmpty && <p className="mt-0.5 text-[13px] text-neutral-500">{ALTITUDE_BLURB[altitude]}</p>}
              {pageTitle && titlePeriod && (
                <p className="mt-0.5 text-[12px] text-neutral-500">Your page says <b className="font-semibold text-neutral-700">{pageTitle}</b></p>
              )}
              {periodChip && <div className="mt-1.5">{periodChip}</div>}
              {!isEmpty && (
                <div role="radiogroup" aria-label="This page is" className="mt-2 flex flex-wrap items-center gap-1.5 text-[12px]">
                  <span className="text-neutral-500">This page is</span>
                  {DOMAINS.map((d) => (
                    <label
                      key={d.id}
                      className={`cursor-pointer rounded-full border px-2.5 py-0.5 font-medium transition-colors ${
                        domain === d.id ? 'border-primary-300 bg-primary-50 text-primary-800' : 'border-neutral-200 bg-white text-neutral-600'
                      }`}
                    >
                      <input
                        type="radio"
                        name="page-domain"
                        value={d.id}
                        checked={domain === d.id}
                        onChange={() => setDomain(d.id)}
                        aria-label={d.label}
                        className="sr-only"
                      />
                      {d.label}
                    </label>
                  ))}
                  <span className="text-neutral-400">
                    {domain === 'family' ? 'Family pages are shared with everyone in the house.' : 'Only you will see this page.'}
                  </span>
                </div>
              )}
            </div>
          </div>
          <button type="button" onClick={onClose} aria-label="Close review" className="p-1.5 rounded-lg text-neutral-400 hover:text-neutral-700 hover:bg-neutral-100 transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        {isEmpty ? (
          <div className="px-5 py-10 text-center text-neutral-500 text-[15px]">
            Couldn&rsquo;t read anything on this page. Try a straighter, brighter scan.
          </div>
        ) : (
          <div className="flex-1 overflow-y-auto px-5 py-3 space-y-4">
            {summary && (
              <div className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-primary-100 bg-primary-50/50 px-3 py-2 text-[13px] text-primary-800">
                <span><span className="font-semibold">Ready to add:</span> {summary}</span>
                {itemRows.some(retypable) && !selecting && (
                  <button type="button" onClick={() => setSelecting(true)} className="shrink-0 font-medium text-primary-700 hover:underline">Select several</button>
                )}
              </div>
            )}
            {selecting && (
              <div role="toolbar" aria-label="Change selected lines" className="sticky top-0 z-10 flex flex-wrap items-center gap-2 rounded-xl border border-neutral-200 bg-white px-3 py-2 text-[13px] shadow-sm">
                <span className="font-semibold text-neutral-800">{selected.size} selected</span>
                <button type="button" className="text-primary-700 hover:underline" onClick={() => setSelected(new Set(itemRows.map((_, i) => i)))}>Select all</button>
                <span className="text-neutral-300" aria-hidden="true">·</span>
                <span className="text-neutral-500">Make them:</span>
                {PAPER_ITEM_TYPES.map((t) => (
                  <button key={t.id} type="button" disabled={!selected.size} onClick={() => bulkType(t.id)}
                    className="rounded-md border border-neutral-200 bg-white px-2 py-1 text-neutral-700 hover:border-neutral-300 hover:text-neutral-950 disabled:opacity-40">
                    {t.label}
                  </button>
                ))}
                <span className="text-neutral-300" aria-hidden="true">·</span>
                <button type="button" disabled={!selected.size} onClick={() => bulkInclude(false)} className="text-neutral-600 hover:underline disabled:opacity-40">Don&rsquo;t add</button>
                <button type="button" disabled={!selected.size} onClick={() => bulkInclude(true)} className="text-neutral-600 hover:underline disabled:opacity-40">Add</button>
                <button type="button" onClick={endSelecting} className="ml-auto font-medium text-primary-700 hover:underline">Done</button>
              </div>
            )}
            {itemRows.length > 0 && (
              <div className="space-y-2">
                {itemRows.map((row, i) => (
                  <div key={`i-${i}`} className={`flex flex-col gap-2 rounded-xl border border-neutral-200/70 px-3 py-2.5 sm:flex-row sm:items-center sm:gap-3 ${row.included ? 'bg-white' : 'bg-neutral-50 opacity-60'}`}>
                    <div className="flex min-w-0 flex-1 items-start gap-3">
                      {selecting && (
                        <button type="button" role="checkbox" aria-checked={selected.has(i)} aria-label={`Select "${row.title}"`}
                          onClick={() => toggleSelected(i)}
                          className={`mt-0.5 grid h-5 w-5 shrink-0 place-items-center rounded-full border-[1.5px] ${selected.has(i) ? 'border-neutral-900 bg-neutral-900 text-white' : 'border-neutral-300 bg-white'}`}>
                          {selected.has(i) && <Check className="h-3 w-3" strokeWidth={3} aria-hidden="true" />}
                        </button>
                      )}
                      <input
                        type="checkbox"
                        checked={row.included}
                        onChange={(e) => updateItem(i, { included: e.target.checked })}
                        aria-label={`Include "${row.title}"`}
                        className="mt-1 w-4 h-4 accent-primary-600 shrink-0"
                      />
                      <div className="min-w-0 flex-1">
                        <div className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1">
                          {/* What this line IS, once, on the left: the one
                              control that decides what it is saved as. A linked
                              line is the existing item — it has no type here. */}
                          {row.kind === 'dayfact'
                            ? <span className="inline-flex shrink-0 items-center rounded-md border border-neutral-200 bg-neutral-50 px-1.5 py-0.5 text-[10px] font-semibold leading-none text-neutral-500">Day</span>
                            : row.sourceId
                              ? <span className="inline-flex shrink-0 items-center gap-1 rounded-md border border-neutral-200 bg-neutral-50 px-1.5 py-1 text-[11px] font-semibold leading-none text-neutral-600"><Link2 className="w-3 h-3" aria-hidden="true" />Existing item</span>
                              : <ItemTypeSelect value={itemTypeOf(row)} title={row.title} onChange={(t) => changeType(i, t)} />}
                          <input
                            value={row.title}
                            readOnly={!!row.sourceId}
                            onChange={(e) => updateItem(i, { title: e.target.value })}
                            aria-label="Task title"
                            className="min-w-[11rem] flex-1 bg-transparent text-[15px] text-neutral-900 focus:outline-none"
                          />
                        </div>
                        {row.note && <p className="mt-1 text-[13px] text-neutral-500 line-clamp-2">{row.note}</p>}
                        {/* A routine's pattern is its when: the days it repeats. */}
                        {row.kind === 'recurring' && !row.sourceId && (
                          <RoutineDaysPicker
                            title={row.title}
                            days={row.recurring?.days ?? []}
                            invalid={row.included && !!itemTypeProblem(row)}
                            onChange={(days) => updateItem(i, { recurring: { days, until: row.recurring?.until ?? null } })}
                          />
                        )}
                        {/* The same errand, written twice: one tap says which.
                            Reuse only skips the insert — it never files the
                            existing item under a goal (that is "Add an existing
                            action" on the goal itself). */}
                        {row.dup && !row.dupDismissed && (
                          row.sourceId
                            ? (
                              <p role="status" className="mt-1 flex flex-wrap items-center gap-1.5 text-[12px] text-neutral-600">
                                <span>Using <i className="text-neutral-800">{row.dup.title}</i>, already on your plan, exactly as it is — nothing new is saved for this line, and it is not put under a goal.</span>
                                <button
                                  type="button"
                                  onClick={() => updateItem(i, { sourceId: undefined })}
                                  className="rounded-md border border-neutral-200 bg-white px-1.5 py-0.5 font-medium text-neutral-700 hover:bg-neutral-50"
                                >
                                  Don&rsquo;t use it
                                </button>
                              </p>
                            )
                            : (
                              <div className="mt-1 text-[12px] text-neutral-500">
                                <p className="flex flex-wrap items-center gap-1.5">
                                  Looks like <i className="text-neutral-700">{row.dup.title}</i>, already on your plan.
                                  <button
                                    type="button"
                                    onClick={() => updateItem(i, { sourceId: row.dup?.id })}
                                    aria-describedby={`dup-why-${i}`}
                                    className="rounded-md border border-neutral-200 bg-white px-1.5 py-0.5 font-medium text-neutral-700 hover:bg-neutral-50"
                                  >
                                    Use existing item
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => updateItem(i, { dupDismissed: true })}
                                    className="rounded-md px-1 py-0.5 text-neutral-500 hover:text-neutral-700"
                                  >
                                    Add as new
                                  </button>
                                </p>
                                <p id={`dup-why-${i}`} className="mt-0.5 text-[11px] text-neutral-400">Avoids a duplicate. It doesn&rsquo;t put the item under a goal.</p>
                              </div>
                            )
                        )}
                      </div>
                    </div>
                    <div className="ml-7 flex flex-wrap items-center gap-2 sm:ml-0 sm:shrink-0 sm:flex-nowrap">
                      {!row.sourceId && (() => {
                        const type = itemTypeOf(row)
                        if (row.kind === 'dayfact') return null
                        // A goal sits on a list, never on a day.
                        if (type === 'goal') return (
                          <select
                            value={placementValue(row.placement)}
                            onChange={(e) => updateItem(i, { placement: placementFromValue(e.target.value), goal: e.target.value !== 'goal' })}
                            aria-label={`Goal for "${row.title}"`}
                            className="text-[13px] text-neutral-700 bg-neutral-100 rounded-lg px-2 py-1.5 shrink-0"
                          >
                            {(altitude === 'year' || row.placement.kind === 'goal') && <option value="goal">Goal for {today.getFullYear()}</option>}
                            <option value="season">Goal for {seasonName}</option>
                            <option value="month">Goal for {monthName}</option>
                          </select>
                        )
                        // A routine's when is its days (above).
                        if (type === 'routine') return null
                        // An appointment is on a day — any day, not only the page's.
                        if (type === 'appointment') return (
                          <input
                            type="date"
                            required
                            value={row.placement.kind === 'date' ? row.placement.date : ''}
                            onChange={(e) => updateItem(i, e.target.value
                              ? { placement: { kind: 'date', date: e.target.value } }
                              : { placement: undatedPlacement(altitude), time: null })}
                            aria-label={`Day of "${row.title}"`}
                            aria-invalid={row.included && row.placement.kind !== 'date'}
                            className={`text-[13px] text-neutral-700 bg-neutral-100 rounded-lg px-2 py-1.5 shrink-0 ${row.included && row.placement.kind !== 'date' ? 'ring-1 ring-danger-500' : ''}`}
                          />
                        )
                        return (
                          <select
                            value={placementValue(row.placement)}
                            onChange={(e) => {
                              const placement = placementFromValue(e.target.value)
                              // A time only lives on a real date. Moving a row to
                              // This week / Inbox must drop it, or a stale "14:00"
                              // rides along on a row that no longer shows one.
                              updateItem(i, { placement, ...(placement.kind === 'date' ? {} : { time: null }) })
                            }}
                            aria-label="When"
                            className="text-[13px] text-neutral-700 bg-neutral-100 rounded-lg px-2 py-1.5 shrink-0"
                          >
                            {HORIZON_OPTIONS.map((o) => (
                              <option key={o.value} value={o.value}>{o.label}</option>
                            ))}
                            {/* A date chosen elsewhere (an appointment's) stays selectable. */}
                            {row.placement.kind === 'date' && !windowNow.includes(row.placement.date) && (
                              <option value={row.placement.date}>{dateLabel(row.placement.date)}</option>
                            )}
                            {windowNow.map((d) => (
                              <option key={d} value={d}>{dateLabel(d)}</option>
                            ))}
                          </select>
                        )
                      })()}
                      {/* A time belongs to an action, appointment or activity on
                          a day, or a routine — never a goal, a linked line, or a
                          day-fact (saved as a note, where a time goes nowhere). */}
                      {!row.sourceId && row.kind !== 'dayfact' && (row.placement.kind === 'date' || row.kind === 'recurring') && !isGoalRow(row) && (
                        <input
                          type="time"
                          value={row.time ?? ''}
                          onChange={(e) => updateItem(i, { time: e.target.value || null })}
                          aria-label={`Time for "${row.title}"`}
                          className="text-[13px] text-neutral-700 bg-neutral-100 rounded-lg px-2 py-1.5 shrink-0 w-auto min-w-[104px] max-w-full"
                        />
                      )}
                      {/* Every row but a day-fact (a note) says who — Year goals
                          included, which take assignees since #64. */}
                      {row.kind !== 'dayfact' && <select
                        disabled={!!row.sourceId}
                        value={row.assigneeId ?? UNASSIGNED}
                        onChange={(e) => updateItem(i, { assigneeId: e.target.value === UNASSIGNED ? null : e.target.value, assigneeDefaulted: false })}
                        aria-label={`Assignee for "${row.title}"`}
                        className="text-[13px] text-neutral-700 bg-neutral-100 rounded-lg px-2 py-1.5 shrink-0 max-w-[140px]"
                      >
                        {peopleOptions.map((o) => (
                          <option key={o.value} value={o.value}>{o.label}</option>
                        ))}
                      </select>}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {(!meId || typeCounts.appointment > 0 || (draftLabel && onAddToDraft && savedDirectly > 0)) && (
              <div className="space-y-1 text-[12px] text-neutral-500">
                {!meId && <p>Symphony couldn&rsquo;t confirm which household member you are, so lines without a name start Unassigned.</p>}
                {typeCounts.appointment > 0 && <p>Appointments are saved in Symphony only. Nothing is added to Google Calendar.</p>}
                {draftLabel && onAddToDraft && savedDirectly > 0 && (
                  <p>{altitude === 'week'
                    ? 'Appointments, activities, routines and goals are saved directly. Actions join the plan you’re writing.'
                    : 'Appointments, activities, routines and goals for another period are saved directly. Actions and this period’s goals join the plan you’re writing.'}</p>
                )}
              </div>
            )}

            {alreadyOnCalendar.length > 0 && (
              <div className="space-y-1.5">
                <p className="flex items-center gap-1.5 text-[13px] font-medium text-neutral-500">
                  <CalendarCheck2 className="w-3.5 h-3.5" />
                  Already on your calendar
                </p>
                {alreadyOnCalendar.map((fact, i) => (
                  <p key={`c-${i}`} className="rounded-xl border border-dashed border-neutral-200 px-3 py-2 text-[13px] text-neutral-500">
                    {fact.title}
                    {fact.placement.kind === 'date' && <span className="text-neutral-400"> · {dateLabel(fact.placement.date)}</span>}
                  </p>
                ))}
              </div>
            )}

            {noteRows.length > 0 && (
              <div className="space-y-2">
                <p className="text-[13px] font-medium text-neutral-500">Notes</p>
                {noteRows.map((row, i) => (
                  <div key={`n-${i}`} className={`flex items-start gap-3 rounded-xl border border-neutral-200/70 px-3 py-2 ${row.included ? 'bg-white' : 'bg-neutral-50 opacity-60'}`}>
                    <input
                      type="checkbox"
                      checked={row.included}
                      onChange={(e) => updateNote(i, { included: e.target.checked })}
                      aria-label={`Include note "${row.title}"`}
                      className="w-4 h-4 accent-primary-600 shrink-0 mt-1"
                    />
                    <div className="flex-1 min-w-0 space-y-1">
                      <input
                        value={row.title}
                        onChange={(e) => updateNote(i, { title: e.target.value })}
                        aria-label="Note title"
                        className="w-full bg-transparent text-[15px] text-neutral-900 focus:outline-none"
                      />
                      <textarea
                        value={row.content}
                        onChange={(e) => updateNote(i, { content: e.target.value })}
                        aria-label="Note content"
                        rows={3}
                        className="w-full bg-transparent text-[13px] text-neutral-600 focus:outline-none resize-y"
                      />
                    </div>
                  </div>
                ))}
              </div>
            )}

            {unread.length > 0 && (
              <div className="space-y-2">
                <p className="text-[13px] font-medium text-neutral-500 flex items-center gap-1.5">
                  <HelpCircle className="w-3.5 h-3.5" />
                  Couldn&rsquo;t read these
                </p>
                {unread.map((line) => (
                  <div key={line} className="flex items-center gap-3 rounded-xl border border-dashed border-neutral-300 px-3 py-2">
                    <span className="flex-1 min-w-0 text-[14px] text-neutral-500 truncate">{line}</span>
                    <button
                      type="button"
                      onClick={() => promoteToTask(line)}
                      aria-label={`Make "${line}" a task`}
                      className="text-[13px] px-2.5 py-1 rounded-lg text-neutral-700 bg-neutral-100 hover:bg-neutral-200 transition-colors shrink-0"
                    >
                      Task
                    </button>
                    <button
                      type="button"
                      onClick={() => promoteToNote(line)}
                      aria-label={`Keep "${line}" as a note`}
                      className="text-[13px] px-2.5 py-1 rounded-lg text-neutral-700 bg-neutral-100 hover:bg-neutral-200 transition-colors shrink-0"
                    >
                      Note
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {!isEmpty && problems > 0 && (
          <p role="alert" className="flex items-center gap-1.5 px-5 pt-3 text-[12px] font-medium text-danger-600">
            <AlertCircle className="w-3.5 h-3.5 shrink-0" />
            {problemText} before this page can be added.
          </p>
        )}
        <div className="flex flex-wrap items-center justify-end gap-2 px-5 py-4 border-t border-neutral-200/60 min-w-0">
          <button type="button" onClick={onClose} className="px-4 py-2 rounded-lg text-[14px] text-neutral-600 hover:bg-neutral-100 transition-colors">
            Cancel
          </button>
          {!isEmpty && draftLabel && onAddToDraft && (
            <button
              type="button"
              onClick={addToDraft}
              disabled={blocked}
              className="btn-primary px-4 py-2 rounded-lg text-[14px] disabled:opacity-50 max-w-full"
            >
              <span className="break-words">{`Add to the plan I’m writing (${draftLabel})`}</span>
            </button>
          )}
          {!isEmpty && (
            <button
              type="button"
              onClick={commit}
              disabled={blocked}
              className="btn-primary px-4 py-2 rounded-lg text-[14px] disabled:opacity-50"
            >
              {committing ? 'Adding…' : `Add ${includedCount} ${includedCount === 1 ? 'item' : 'items'}`}
            </button>
          )}
        </div>
      </div>
    </div>
  )
}
